/**
 * The audit log: every action anyone took through the CRM, newest first, with who, when, from where
 * and whether it worked - filtered, paged backwards, exportable.
 *
 * The port of `Views/AuditPage.xaml` + `AuditViewModel`. Labels and target links are `audit.ts`,
 * pinned to the generated enums by its tests.
 *
 *   * ADMIN-ONLY; a deep link from a technician renders the sentence saying so.
 *   * IT SHOWS A PAGE, NOT THE LOG. The count line says what is not on screen - a hundred rows with no
 *     total above them read as a quiet week.
 *   * PAGING IS BY ID, backwards from the oldest row on screen, not by offset: entries keep arriving
 *     while someone reads, and an offset would slide the page under them.
 *   * Refusals and failures are set apart (an amber or red outcome label) so they can be picked out of
 *     a run of routine rows, and a row whose target is a page here links to it.
 *   * The CSV export is itself recorded, with the filter it was run under - a log that could be read
 *     out without a trace would be missing its own most interesting row.
 */
import type { Container } from 'webwin';
import { isAdmin } from '../api/auth.js';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import { ACTOR_KINDS, ALL_CATEGORIES, actorKindLabel, categoryLabel, outcomeLabel, targetLink } from '../audit.js';
import { AuditOutcome, type AuditEntryDto } from '../contracts.gen.js';
import { download } from '../download.js';
import { localDateTime } from '../format.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { StatusLine } from '../ui/StatusLine.js';
import type { TextRole } from '../ui/Text.js';
import {
  button,
  buttonRow,
  card,
  comboBox,
  field,
  FONT,
  GAP,
  h1,
  muted,
  relayout,
  scrollPage,
  stackOf,
  text,
  textBox,
  value,
  vstack
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

const PAGE_SIZE = 100;
const OUTCOMES = [undefined, AuditOutcome.Success, AuditOutcome.Refused, AuditOutcome.Failed] as const;
const RANGES: readonly { label: string; hours: number | null }[] = [
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 7 days', hours: 24 * 7 },
  { label: 'Last 30 days', hours: 24 * 30 },
  { label: 'Last 90 days', hours: 24 * 90 },
  { label: 'All time', hours: null }
];

function outcomeRole(outcome: AuditOutcome): TextRole {
  return outcome === AuditOutcome.Refused ? 'warn' : outcome === AuditOutcome.Failed ? 'danger' : 'ok';
}

export function auditPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      const { view, content } = scrollPage();
      host.controls.add(view);

      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        h1('Audit log'),
        muted('Every action taken through the CRM - sign-ins, remote sessions, changes and exports - including every request that was refused.')
      );
      content.controls.add(header);

      if (!isAdmin()) {
        const notice = card();
        notice.controls.add(
          text(
            "The audit log is administrator-only. It records every action taken through the CRM - including yours - and reading it is the administrator's call."
          )
        );
        content.controls.add(notice);
        relayout(content);
        return;
      }

      let rows: AuditEntryDto[] = [];
      const status = new StatusLine();

      // ---- filters ----
      const searchBox = textBox({ placeholder: 'Summary, detail, target, address…' });
      const actorBox = textBox({ placeholder: 'Name or address' });
      for (const box of [searchBox, actorBox]) {
        box.on('keydown', (e: { key: string }) => {
          if (e.key === 'Enter') void load();
        });
      }
      const kindPicker = comboBox<string>(['All actors', ...ACTOR_KINDS.map(actorKindLabel)], {
        selectedIndex: 0,
        onSelectionChanged: () => void load()
      });
      const categoryPicker = comboBox<string>(['All areas', ...ALL_CATEGORIES.map(categoryLabel)], {
        selectedIndex: 0,
        onSelectionChanged: () => void load()
      });
      const outcomePicker = comboBox<string>(['All outcomes', 'OK', 'Refused', 'Failed'], {
        selectedIndex: 0,
        onSelectionChanged: () => void load()
      });
      const rangePicker = comboBox<string>(RANGES.map(r => r.label), {
        selectedIndex: 1,
        onSelectionChanged: () => void load()
      });
      let quiet = false; // set while Reset moves the pickers, so they do not each trigger a load

      // Under the title, not beside it: a wrapping row there is measured to one button's width.
      header.controls.add(
        stackOf(buttonRow(), button('Refresh', () => void load()), button('Export CSV', () => void exportCsv()))
      );
      header.controls.add(status.label);

      const summary = text('');
      const empty = muted('');
      const olderButton = button('Load older entries', () => void load(true));
      olderButton.visible = false;

      const filters = card();
      filters.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          stackOf(
            buttonRow({ spacing: GAP.md, align: 'end' }),
            field('Search', searchBox, { width: 240 }),
            field('Actor', actorBox, { width: 180 }),
            field('Actor kind', kindPicker, { width: 160 }),
            field('Area', categoryPicker, { width: 170 }),
            field('Outcome', outcomePicker, { width: 140 }),
            field('When', rangePicker, { width: 150 })
          ),
          stackOf(buttonRow(), button('Apply', () => void load(), true), button('Reset', () => void reset())),
          summary
        )
      );

      const list = new CardList<AuditEntryDto>({ key: e => e.id, build: e => buildRow(e), spacing: GAP.sm });

      content.controls.add(
        stackOf(vstack({ spacing: GAP.lg }), filters, empty, list.panel, stackOf(buttonRow(), olderButton))
      );

      function buildRow(e: AuditEntryDto): Card {
        const heading = buttonRow({ spacing: GAP.sm });
        heading.controls.add(value(outcomeLabel(e.outcome), { font: FONT.bold, role: outcomeRole(e.outcome) }));
        heading.controls.add(value(localDateTime(e.whenUtc), { font: FONT.small, role: 'muted' }));

        const actor = [
          `${e.actor} (${actorKindLabel(e.actorKind)})`,
          categoryLabel(e.category),
          e.ipAddress?.trim() ? `from ${e.ipAddress}` : null,
          e.statusCode > 0 ? `${e.method} ${e.statusCode}` : e.method || null,
          e.durationMs > 0 ? `${e.durationMs} ms` : null
        ]
          .filter(Boolean)
          .join(' - ');

        // A refusal or a failure is bold, so it can be picked out of a run of routine rows.
        const notable = e.outcome !== AuditOutcome.Success;
        const body = stackOf(vstack({ spacing: GAP.xs }), heading, text(e.summary, notable ? { font: FONT.bold } : {}), muted(actor));
        if (e.detail?.trim()) body.controls.add(muted(e.detail, 'subtle'));
        const link = targetLink(e.target);
        if (link) body.controls.add(stackOf(buttonRow(), button(link.label, () => ctx.go(link.path))));

        const shell = card();
        shell.controls.add(body);
        return shell;
      }

      function filter(beforeId?: number): crm.AuditFilter {
        const kindIndex = kindPicker.selectedIndex;
        const categoryIndex = categoryPicker.selectedIndex;
        const range = RANGES[rangePicker.selectedIndex] ?? RANGES[1];
        return {
          actorKind: kindIndex > 0 ? ACTOR_KINDS[kindIndex - 1] : undefined,
          actor: actorBox.text,
          category: categoryIndex > 0 ? ALL_CATEGORIES[categoryIndex - 1] : undefined,
          outcome: OUTCOMES[outcomePicker.selectedIndex] ?? undefined,
          search: searchBox.text,
          fromUtc: range?.hours ? new Date(Date.now() - range.hours * 3_600_000).toISOString() : undefined,
          beforeId,
          limit: PAGE_SIZE
        };
      }

      function describeFilter(): string {
        const parts: string[] = [];
        if (kindPicker.selectedIndex > 0) parts.push(`${(kindPicker.items[kindPicker.selectedIndex] ?? '').toLowerCase()} actors`);
        if (actorBox.text.trim()) parts.push(`actor '${actorBox.text.trim()}'`);
        if (categoryPicker.selectedIndex > 0) parts.push(categoryPicker.items[categoryPicker.selectedIndex] ?? '');
        if (outcomePicker.selectedIndex > 0) parts.push((outcomePicker.items[outcomePicker.selectedIndex] ?? '').toLowerCase());
        if (searchBox.text.trim()) parts.push(`'${searchBox.text.trim()}'`);
        const when = (RANGES[rangePicker.selectedIndex]?.label ?? '').toLowerCase();
        return (parts.length ? ` for ${parts.join(', ')}` : '') + (when === 'all time' ? '' : ` in the ${when}`);
      }

      async function load(older = false): Promise<void> {
        if (quiet) return;
        const before = older && rows.length > 0 ? rows[rows.length - 1]?.id : undefined;
        try {
          const page = await crm.getAudit(filter(before), ctx.dispose.signal());
          if (ctx.cancelled()) return;
          rows = older ? [...rows, ...page.entries] : page.entries;
          list.set(rows);
          olderButton.visible = page.hasMore;
          const n = (x: number): string => x.toLocaleString('en-US');
          summary.text =
            page.totalStored === 0
              ? ''
              : `${n(page.totalMatching)} matching${describeFilter()}; ${n(page.totalStored)} recorded in total` +
                (rows.length < page.totalMatching ? `, showing the newest ${n(rows.length)}.` : '.');
          empty.text =
            rows.length > 0 ? '' : page.totalStored === 0 ? 'Nothing has been recorded yet.' : 'Nothing recorded matches these filters.';
          empty.visible = empty.text.length > 0;
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the audit log: ' + describeError(error));
        }
      }

      async function reset(): Promise<void> {
        quiet = true;
        searchBox.text = '';
        actorBox.text = '';
        kindPicker.selectedIndex = 0;
        categoryPicker.selectedIndex = 0;
        outcomePicker.selectedIndex = 0;
        rangePicker.selectedIndex = 1;
        quiet = false;
        await load();
      }

      async function exportCsv(): Promise<void> {
        status.busy('Preparing the CSV…');
        try {
          await download(crm.auditCsvPath(filter()), 'JetsonCRM-Audit.csv');
          if (ctx.cancelled()) return;
          status.ok('CSV downloaded (the export is itself recorded).');
          void load();
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not download the CSV: ' + describeError(error));
        }
      }

      void load();
    }
  };
}
